/**
 * Create/edit drawer for a business location (UI pass spec 2026-07-26 §3).
 * Absorbs the form/validate/save logic that used to live inline in
 * BusinessLocationsTab. Renders null when closed so every open starts with
 * fresh state initialized from `location`.
 */

import { useState } from 'react'
import {
  Alert, AlertDescription, Button, Drawer, DrawerBody, DrawerContent,
  DrawerFooter, DrawerHeader, DrawerTitle, Input, Separator, Switch, Textarea,
} from '@/components/ui'
import { WeeklyHoursEditor } from '@/components/admin/WeeklyHoursEditor'
import { apiClient, ApiError } from '@/services/apiClient'
import type { BusinessLocation, WeeklyHours } from '@/types/commerce'

/** Advisory geocode result from POST /business-locations/validate-address. */
type AddressValidation = {
  status: string
  formatted_address: string | null
  lat: number | null
  lng: number | null
  location_type: string | null
  partial_match: boolean | null
}

interface LocationFormDrawerProps {
  open: boolean
  location: BusinessLocation | null
  onClose: () => void
  onSaved: () => void
}

function SectionLabel({ children }: { children: string }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </h3>
  )
}

export function LocationFormDrawer(props: LocationFormDrawerProps) {
  if (!props.open) return null
  return <LocationFormDrawerInner {...props} />
}

function LocationFormDrawerInner({ location, onClose, onSaved }: LocationFormDrawerProps) {
  const [formName, setFormName] = useState(location?.name ?? '')
  const [formAddress, setFormAddress] = useState(location?.address ?? '')
  const [formPickup, setFormPickup] = useState(location?.pickupEnabled ?? false)
  const [formPickupHours, setFormPickupHours] = useState<WeeklyHours | null>(
    location?.pickupHours ?? null,
  )
  const [formDelivery, setFormDelivery] = useState(location?.deliveryEnabled ?? false)
  const [formDeliveryHours, setFormDeliveryHours] = useState<WeeklyHours | null>(
    location?.deliveryHours ?? null,
  )
  const [formCoverage, setFormCoverage] = useState(
    (location?.deliveryCoverage ?? []).join('\n'),
  )
  const [formLat, setFormLat] = useState(location?.lat != null ? String(location.lat) : '')
  const [formLng, setFormLng] = useState(location?.lng != null ? String(location.lng) : '')
  const [formHoursText, setFormHoursText] = useState(location?.hoursText ?? '')
  const [formNotes, setFormNotes] = useState(location?.notes ?? '')
  const [formSort, setFormSort] = useState(String(location?.sortOrder ?? 0))
  const [formSaving, setFormSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validating, setValidating] = useState(false)
  const [validation, setValidation] = useState<AddressValidation | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)

  /** Advisory only (spec §6.3/§6.4): never blocks a save, never touches the
   * `error` state used for save failures. */
  const handleValidate = async () => {
    if (!formAddress.trim()) return
    setValidating(true)
    setValidation(null)
    setValidationError(null)
    try {
      const result = await apiClient.post<AddressValidation>(
        '/api/commerce/business-locations/validate-address',
        { address: formAddress },
      )
      setValidation(result)
    } catch (e) {
      if (e instanceof ApiError && typeof e.detail === 'string' && e.detail) {
        setValidationError(e.detail)
      } else {
        setValidationError(e instanceof Error ? e.message : 'Address validation failed')
      }
    } finally {
      setValidating(false)
    }
  }

  const applyValidation = (useCorrected: boolean) => {
    if (!validation) return
    if (useCorrected && validation.formatted_address) {
      setFormAddress(validation.formatted_address)
    }
    if (validation.lat != null) setFormLat(String(validation.lat))
    if (validation.lng != null) setFormLng(String(validation.lng))
    setValidation(null)
  }

  const handleSave = async () => {
    if (!formName || !formAddress) return
    setFormSaving(true)
    setError(null)
    const body = {
      name: formName,
      address: formAddress,
      pickup_enabled: formPickup,
      pickup_hours: formPickup ? formPickupHours : null,
      delivery_enabled: formDelivery,
      delivery_hours: formDelivery ? formDeliveryHours : null,
      delivery_coverage: (() => {
        if (!formDelivery) return null
        const areas = formCoverage
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
        return areas.length ? areas : null
      })(),
      lat: formLat.trim() !== '' ? parseFloat(formLat) : null,
      lng: formLng.trim() !== '' ? parseFloat(formLng) : null,
      hours_text: formHoursText || null,
      notes: formNotes || null,
      sort_order: parseInt(formSort) || 0,
    }
    try {
      if (location) {
        await apiClient.put(`/api/commerce/business-locations/${location.id}`, body)
      } else {
        await apiClient.post('/api/commerce/business-locations', body)
      }
      onSaved()
    } catch (e) {
      if (e instanceof ApiError && typeof e.detail === 'string' && e.detail) {
        setError(e.detail)
      } else {
        setError(e instanceof Error ? e.message : 'Save failed')
      }
    } finally {
      setFormSaving(false)
    }
  }

  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose() }}>
      <DrawerContent className="max-w-xl">
        <DrawerHeader>
          <DrawerTitle>{location ? `Edit: ${location.name}` : 'New location'}</DrawerTitle>
        </DrawerHeader>

        <DrawerBody>
          <section className="space-y-3">
            <SectionLabel>Identity</SectionLabel>
            <Input
              label="Name"
              required
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. Polanco"
            />
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor="loc-address" className="block text-sm font-medium text-foreground">
                  Address <span className="text-destructive">*</span>
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleValidate}
                  disabled={validating || !formAddress.trim()}
                  className="h-6 px-2 text-xs font-normal"
                >
                  {validating ? 'Validating...' : 'Validate'}
                </Button>
              </div>
              <Textarea
                id="loc-address"
                aria-label="Address"
                value={formAddress}
                onChange={(e) => {
                  setFormAddress(e.target.value)
                  setValidation(null)
                  setValidationError(null)
                }}
                rows={2}
                placeholder="Street, neighborhood, city, postal code"
              />
            </div>

            {validationError && (
              <p className="text-xs text-destructive">{validationError}</p>
            )}

            {validation && validation.status !== 'found' && (
              <p className="text-xs text-muted-foreground">
                We couldn't match that address. You can still save it as typed.
              </p>
            )}

            {validation && validation.status === 'found' && (
              <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">Google suggests:</p>
                <p className="text-sm text-foreground">{validation.formatted_address}</p>
                {validation.lat != null && validation.lng != null && (
                  <p className="text-xs text-muted-foreground">
                    Coordinates: {validation.lat}, {validation.lng}
                  </p>
                )}
                {(validation.partial_match === true ||
                  validation.location_type === 'APPROXIMATE') && (
                  <p className="text-xs text-destructive">
                    This looks approximate — double-check the coordinates before
                    enabling delivery.
                  </p>
                )}
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={() => applyValidation(true)}
                    className="h-7 px-3 text-xs">
                    Use corrected address
                  </Button>
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => applyValidation(false)}
                    className="h-7 px-3 text-xs font-normal text-muted-foreground">
                    Keep my address
                  </Button>
                </div>
              </div>
            )}
          </section>

          <Separator />

          <section className="space-y-3">
            <SectionLabel>Coordinates</SectionLabel>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Latitude"
                value={formLat}
                onChange={(e) => setFormLat(e.target.value)}
                placeholder="19.3879"
              />
              <Input
                label="Longitude"
                value={formLng}
                onChange={(e) => setFormLng(e.target.value)}
                placeholder="-99.1936"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Optional for pickup-only locations. Required when delivery is enabled —
              they are the delivery origin (courier pickup point).
            </p>
          </section>

          <Separator />

          <section className="space-y-4">
            <SectionLabel>Services</SectionLabel>
            <div className="space-y-3 rounded-lg border border-border p-3">
              <label className="flex items-center gap-2.5 text-sm font-medium text-foreground">
                <Switch
                  checked={formPickup}
                  aria-label="Pickup available"
                  onCheckedChange={setFormPickup}
                />
                Pickup available
              </label>
              {formPickup && (
                <WeeklyHoursEditor
                  value={formPickupHours}
                  onChange={setFormPickupHours}
                  label="Pickup hours"
                />
              )}
            </div>
            <div className="space-y-3 rounded-lg border border-border p-3">
              <label className="flex items-center gap-2.5 text-sm font-medium text-foreground">
                <Switch
                  checked={formDelivery}
                  aria-label="Delivery available"
                  onCheckedChange={setFormDelivery}
                />
                Delivery available
              </label>
              {formDelivery && (
                <>
                  <WeeklyHoursEditor
                    value={formDeliveryHours}
                    onChange={setFormDeliveryHours}
                    label="Delivery hours"
                    copyFrom={formPickup ? { label: 'pickup hours', value: formPickupHours } : undefined}
                  />
                  <p className="text-xs text-muted-foreground">
                    The window in which delivery orders are accepted and delivery times
                    can be scheduled. Need morning prep time? Start the window later.
                  </p>
                  <div>
                    <label htmlFor="loc-coverage" className="mb-1.5 block text-sm font-medium text-foreground">
                      Delivery coverage areas
                    </label>
                    <Textarea
                      id="loc-coverage"
                      value={formCoverage}
                      onChange={(e) => setFormCoverage(e.target.value)}
                      rows={4}
                      placeholder={'Ciudad de México\nNaucalpan de Juárez'}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      One per line, official names as maps return them. Empty = no
                      restriction.
                    </p>
                  </div>
                </>
              )}
            </div>
          </section>

          <Separator />

          <section className="space-y-3">
            <SectionLabel>Display</SectionLabel>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Hours (display text)"
                value={formHoursText}
                onChange={(e) => setFormHoursText(e.target.value)}
                placeholder="Lun–Sáb 9:00–19:00"
              />
              <Input
                label="Notes"
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Parking, landmarks"
              />
            </div>
            <Input
              label="Sort Order"
              type="number"
              value={formSort}
              onChange={(e) => setFormSort(e.target.value)}
              className="max-w-32"
            />
          </section>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </DrawerBody>

        <DrawerFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={formSaving || !formName || !formAddress}
            isLoading={formSaving}
          >
            {location ? 'Update' : 'Create'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
